import React from 'react';
import { MetricsElement } from "react-metrics";
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { push } from 'connected-react-router';

import { Helpers } from '../../core/src/helpers';
import { withError } from '../../shared/components/error-boundary';
import ImageWithPlaceholder from '../../shared/components/image-with-placeholder';
import AccessibleElement from '../../ui/components/accessible-element';
import KspList from '../../product/components/ksp-list';
import ProductPrice from '../../product/components/product-price';
import { ModalControl } from "../../context/modal/modal-context";
import withPrices from '../../product/components/product-price/with-product-price';

import { OpenModal } from './pdp-oos-popup';

import '../css/pdp-upsell.less';

class UpsellModal extends React.Component {

    static propTypes = {
        productSlug: PropTypes.string.isRequired,
        upsell: PropTypes.object.isRequired
    }

    state = {
        selected: this.props.upsell.productsList.length === 1 ? this.props.upsell.productsList[0].sku : null
    }

    componentDidUpdate(prevProps) {
        if (this.props.closeOOSModal && prevProps.sku !== this.props.sku) {
            this.props.closeOOSModal(this.props.closeModal, true);
        }
    }

    changeProduct = (sku) => {
        this.setState({ selected: sku });
    }

    mergeProducts = () => {
        const { productsList } = this.props.upsell;
        const mergedList = productsList.map(upsellProd => PDPUpsell.mergeUpsellProd(upsellProd, upsellProd.metadata));
        return mergedList;
    }

    continue = () => {

        let { selected } = this.state;
        let { isOOS, closeOOSModal, closeModal, productSlug, pushHistory } = this.props;

        if (!selected) return;
        const selectedPrd = this.upsellProducts.find(prod => prod.sku === selected);
        const selectedSlug = selectedPrd.slug;

        if (productSlug !== selectedSlug) {
            let pdpLink = `${process.env.BASENAME}/pdp/${selectedSlug}`;
            //if link url is external, dont use react router
            if (selectedPrd.linkUrl && selectedPrd.linkUrl.indexOf(pdpLink) < 0) {
                document.location = selectedPrd.linkUrl
                return;
            }
            pushHistory(pdpLink);
        }

        if (isOOS && closeOOSModal) {
            return closeOOSModal(closeModal);
        }
        closeModal();
    }

    render() {
        this.upsellProducts = this.mergeProducts();
        const metaData = this.props.upsell.metaData || {};
        const baseSku = this.props.sku;
        let btnClassName = `btn btn-primary${!this.state.selected ? " disabled" : ""} unstyled`;
        return (
            <div className='upsell-modal'>
                <div tabIndex={0} aria-label="Select a similar product" className='upsell-modal-header'>{metaData.modalHeader || 'Select a similar product'}</div>
                <div className='upsell-table-container'>
                    {this.upsellProducts.map((prod, i) => {
                        try {
                            const [prdImage = {}] = prod.images;
                            const imageSrc = Helpers.getCDNUrl(prdImage.cdn, prdImage.lrg);
                            const shippingText = prod.shippingText || prod.prdClass === 'CTO' ? 'CONFIGURABLE' : 'READY TO SHIP';
                            const itemSelected = this.state.selected === prod.sku ? ' selected' : '';
                            return (
                                <AccessibleElement
                                    element="div"
                                    ariaLabel={`Similar Item Number ${i + 1}${itemSelected}`}
                                    className={`upsell-item${itemSelected}`}
                                    key={`${prod.sku}-${i}`}
                                    onClick={() => this.changeProduct(prod.sku)}
                                >
                                    <ImageWithPlaceholder tabindex={0} className='upsell-image-item' src={imageSrc} />
                                    <div aria-label={prod.name} tabIndex={0} className='upsell-product-title'>{prod.name}</div>
                                    <div className='upsell-product-diff'>{prod.mktdiff || prod.mktDiff}</div>
                                    <KspList ksps={prod.ksps} />
                                    <ProductPrice product={prod} />
                                </AccessibleElement>
                            )
                        } catch (e) { }
                        return null;
                    })}
                </div>
                {this.state.selected ?
                    <MetricsElement>
                        <button
                            data-metrics-event-name="linkClick"
                            data-metrics-event="e_linkClick"
                            data-metrics-link-id={`similar-product${this.props.linkNameSuffix || ''}:continue:base.${baseSku}:upsell.${this.state.selected}`}
                            className={btnClassName}
                            onClick={this.continue}
                        >
                            CONTINUE
                        </button>
                    </MetricsElement> :
                    <div className={btnClassName} onClick={this.continue}>CONTINUE</div>
                }
            </div>
        )
    }
}

/**
* Determines whether the similar products popup should automatically open for a PDP, if the following is true:
* - is OOS
* - is not a CTO
* - is not an ink/toner/paper
* - is not a multi-sku PDP
* - has no oos backup (waits until oosBackup has been fetched first using the asyncDataLoaded flag. 
                        If there are backups, it waits to see if all backups are oos)
*/
const shouldAutoOpen = (product, prices, stateSku) => {
    let { sku = '', prdClass, asyncDataLoaded, oosBackup } = product;
    let isOOS = prices && prices[sku] && prices[sku].stock === 0 && sku === stateSku; // sku === stateSku ensures the current pdp is for the correct sku
    let isNotCTO = prdClass !== 'CTO';
    let isMultiSku = prdClass === 'PRT' && product && product.variants && product.variants.length > 0;
    let isSupplies = product && product.categories instanceof Array && product.categories.includes('70m-W');
    let noBackups = !oosBackup || !oosBackup.productsList || oosBackup.productsList.length === 0 ||
        oosBackup.productsList.every(prd => prd && prd.sku && prices && prices[prd.sku] && prices[prd.sku].stock === 0);

    return asyncDataLoaded && isNotCTO && noBackups && !isSupplies && !isMultiSku && isOOS;
}

class PDPUpsell extends React.Component {

    // oosModalOpened is used to indicate whether the oos popup was opened. 
    // showOOSContent indicates if modal should show oos content (linkName, message) even when oosModalOpened=true after the modal is opened
    state = { sku: null, oosModalOpened: false, showOOSContent: false };

    static mergeUpsellProd = (product, metaData = {}) => {
        return Object.assign({}, product, metaData);
    }

    static propTypes = {
        product: PropTypes.object.isRequired,
        withMetrics: PropTypes.bool,
        title: PropTypes.string
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.product && nextProps.product.sku && nextProps.product.sku !== prevState.sku) {
            return {
                sku: nextProps.product.sku,
                oosModalOpened: false,
                showOOSContent: false
            };
        }
        return null;
    }

    renderOOSModalHeader = () => (
        <div>
            <div>This product is currently <span style={{ color: 'red' }}>out of stock</span></div>
            <div>Here are similar products you might like:</div>
        </div>
    );

    closeModal = (closeModalContext, keepOOSState) => {
        if (this.state.showOOSContent && !keepOOSState) {
            this.setState({ showOOSContent: false });
        }
        closeModalContext();
    }

    openOOSModal = (openModal) => {
        this.setState({ showOOSContent: true, oosModalOpened: true }, openModal);
    }

    render() {
        const { pushHistory, product, modalContent, withMetrics = false, title = 'See similar products', prices, modalOnly } = this.props;
        let { upsell = { productsList: [] }, sku = '' } = product;
        let { oosModalOpened, showOOSContent } = this.state;
        //if parent product, filter variants from upsell list
        if (product.variants && product.variants.length > 0) {
            const variantSkus = product.variants.reduce((r, v) => {
                if (v && v.product && v.product.sku) {
                    r.push(v.product.sku);
                }
                return r;
            }, [])
            upsell.productsList = (upsell.productsList || []).filter(u => !variantSkus.includes(u.sku));
        }
        const upsellCount = upsell.hasOwnProperty('productsList') ? upsell.productsList.length : 0;
        if (upsellCount < 1) {
            return null;
        }

        let autoOpenOnPageLoad = shouldAutoOpen(product, prices, this.state.sku);
        let shouldOpenOOSModal = autoOpenOnPageLoad && !oosModalOpened;
        let upsellProps = showOOSContent
            ? {
                ...upsell,
                metaData: {
                    ...upsell.metaData,
                    modalHeader: this.renderOOSModalHeader()
                }
            }
            : upsell;
        let linkNameSuffix = showOOSContent
            ? '-oos'
            : null;

        return (
            <div className="upsell-container">
                <ModalControl
                    dynamicContent={true}
                    content={modalContent || UpsellModal}
                    contentProps={{
                        closeOOSModal: this.closeModal, linkNameSuffix,
                        upsell: upsellProps, pushHistory, sku,
                        isOOS: autoOpenOnPageLoad, productSlug: product.slug,
                        showOOSContent
                    }}
                    modalProps={{
                        wrapperClassName: "pdp-upsell-bg",
                        className: `upsell-modal-container${autoOpenOnPageLoad && showOOSContent ? ' similar-product-oos' : ''} modal-size-${upsellCount}`,
                        customCloseModal: this.closeModal
                    }}
                >
                    {(props) => {
                        if (modalOnly) {
                            return shouldOpenOOSModal ? <OpenModal openModal={() => this.openOOSModal(props.openModal)} shouldOpen={!oosModalOpened} /> : null
                        } else if (withMetrics) {
                            return (
                                <MetricsElement>
                                    <button
                                        data-metrics-event-name="linkClick"
                                        data-metrics-event="e_linkClick"
                                        data-metrics-link-id={`similar-product:modal-open:${product.sku}`}
                                        className='current-item unstyled'
                                        onClick={props.openModal}
                                    >
                                        <p className='upsell-text'>{title}</p>
                                        {shouldOpenOOSModal && <OpenModal openModal={() => this.openOOSModal(props.openModal)} shouldOpen={!oosModalOpened} />}
                                    </button>
                                </MetricsElement>
                            )
                        } else {
                            return (
                                <div className='current-item' onClick={props.openModal}>
                                    <p className='upsell-text'>{title}</p>
                                </div>
                            )
                        }
                    }}
                </ModalControl>
            </div>
        );
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        pushHistory: (pathname, queryString) => {
            dispatch(push({
                pathname: pathname,
                search: queryString
            }));
        }
    }
};

export { UpsellModal, shouldAutoOpen };
export default connect(null, mapDispatchToProps)(withError(withPrices(PDPUpsell)));